import React, { useState } from 'react';
import { showToast } from '../../utils/helpers';
import { X, Mail, Phone, MapPin } from 'lucide-react';

const ContactStoreModal = ({ show, onHide, store, product }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    subject: 'inquiry'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!show) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEmailContact = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Simulate API call to send email
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const emailSubject = product 
        ? `Inquiry about ${product.product_name} from ${store.company_name}`
        : `General inquiry for ${store.company_name}`;
      
      const emailBody = `
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Message: ${formData.message}

${product ? `Product: ${product.product_name}` : ''}
      `.trim();

      const mailtoLink = `mailto:${store.contact_email || 'store@example.com'}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
      
      window.location.href = mailtoLink;
      showToast('Email client opened successfully', 'success');
      onHide();
      
    } catch (error) {
      showToast('Failed to open email client', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppContact = () => {
    const phoneNumber = store.company_phone || '2341234567890'; // Default Nigerian number format
    const message = product
      ? `Hello, I'm interested in your product: ${product.product_name}. ${formData.message ? `Additional message: ${formData.message}` : ''}`
      : `Hello, I have a general inquiry about your store. ${formData.message ? `Message: ${formData.message}` : ''}`;

    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    showToast('Opening WhatsApp...', 'info');
    onHide();
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      message: '',
      subject: 'inquiry'
    });
    onHide();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-white dark:bg-[#0a0a0a] rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-800">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-2xl font-bold text-black dark:text-white">
            Contact {store.company_name}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6">
          {/* Left Column - Store Details */}
          <div className="bg-gray-50 dark:bg-gray-900/50 p-6 rounded-xl border border-gray-100 dark:border-gray-800">
            <h3 className="text-lg font-bold text-black dark:text-white mb-6">Store Information</h3>
            
            {/* Store Logo/Image */}
            <div className="flex items-center mb-6">
              {store.company_logo ? (
                <img
                  src={store.company_logo}
                  alt={store.company_name}
                  className="w-16 h-16 rounded-full object-cover mr-4 border border-gray-200 dark:border-gray-700"
                />
              ) : (
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-xl font-bold mr-4 shadow-md">
                  {store.company_name?.charAt(0) || 'S'}
                </div>
              )}
              <div>
                <h4 className="text-lg font-bold text-black dark:text-white">{store.company_name}</h4>
                {store.reg_number && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Reg: {store.reg_number}</p>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-5">
              {store.contact_email && (
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mr-3">
                    <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase">Email</p>
                    <p className="text-sm text-black dark:text-white font-bold">{store.contact_email}</p>
                  </div>
                </div>
              )}

              {store.company_phone && (
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mr-3">
                    <Phone className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase">Phone</p>
                    <p className="text-sm text-black dark:text-white font-bold">{store.company_phone}</p>
                  </div>
                </div>
              )}

              {store.company_address && (
                <div className="flex items-start">
                  <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                    <MapPin className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase">Address</p>
                    <p className="text-sm text-black dark:text-white font-bold leading-snug">{store.company_address}</p>
                  </div>
                </div>
              )}

              {store.company_description && (
                <div className="pt-6 border-t border-gray-200 dark:border-gray-800 mt-2">
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase mb-2">About</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{store.company_description}</p>
                </div>
              )}
            </div>

            {/* Product Info if available */}
            {product && (
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800/50">
                <p className="text-xs font-bold text-blue-800 dark:text-blue-300 mb-3 uppercase tracking-wide">Product Inquiry</p>
                <div className="flex items-center">
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.product_name}
                      className="w-14 h-14 object-cover rounded-lg mr-3 border border-blue-200 dark:border-blue-800"
                    />
                  ) : (
                    <div className="w-14 h-14 bg-blue-200 dark:bg-blue-800 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-xl font-bold text-blue-800 dark:text-blue-200">
                        {product.product_name?.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-bold text-black dark:text-white line-clamp-1">{product.product_name}</p>
                    <p className="text-sm text-blue-600 dark:text-blue-400 font-bold mt-0.5">
                      {product.currency === 'NGN' ? '₦' : '$'}{product.per_unit_cost?.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Contact Form */}
          <div>
            <h3 className="text-xl font-bold text-black dark:text-white mb-6">Send a Message</h3>
            
            <form onSubmit={handleEmailContact} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-black dark:text-white transition-colors placeholder-gray-400 text-sm"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-black dark:text-white transition-colors placeholder-gray-400 text-sm"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-black dark:text-white transition-colors placeholder-gray-400 text-sm"
                  placeholder="+234 123 456 7890"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-black dark:text-white transition-colors placeholder-gray-400 text-sm resize-none"
                  placeholder="Tell us about your inquiry..."
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-blue-600 text-white py-3.5 px-4 rounded-full font-bold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg text-sm"
                >
                  {isSubmitting ? 'Opening Email...' : 'Send via Email'}
                </button>

                <button
                  type="button"
                  onClick={handleWhatsAppContact}
                  className="flex-1 bg-green-600 text-white py-3.5 px-4 rounded-full font-bold hover:bg-green-700 transition-colors flex items-center justify-center shadow-lg text-sm"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0.16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.864 3.488"/>
                  </svg>
                  WhatsApp
                </button>
              </div>
            </form>

            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800">
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                <strong>Note:</strong> Your contact information will be shared with the store owner. 
                {store.contact_email && <span className="ml-1">You can also email directly at <a href={`mailto:${store.contact_email}`} className="text-blue-500 hover:underline">{store.contact_email}</a></span>}
                {store.company_phone && <span className="ml-1"> or call <a href={`tel:${store.company_phone}`} className="text-blue-500 hover:underline">{store.company_phone}</a></span>}.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactStoreModal;